/**
 * Befunde der Serverprüfung (`/validate`) als deutsche Sätze am richtigen Feld. Der Server liefert
 * Entwicklertext auf Englisch und als Code nur `pricing_configuration_invalid` (gotchas-aspnet
 * §6.167) – deshalb ordnen bekannte Sätze und Feldnamen zu; Unbekanntes bleibt als allgemeiner Satz
 * mit Feldname sichtbar, der Servertext klein darunter.
 */
import type { PricingConfigurationValidationIssue } from '@/shared/api';
import { CODE_TEXT, FIELD_LABEL, KNOWN } from './configurationIssueCatalog';
import { fieldRoot, sectionOfField, type FieldIssue } from './configurationSections';

function fallback(label: string, message: string): string {
  const between = /between (\d+) and (\d+)/i.exec(message);
  if (between) return `${label} muss zwischen ${between[1]} und ${between[2]} liegen.`;
  const atLeast = /greater than or equal to '?(\d+)'?/i.exec(message);
  if (atLeast) return `${label} darf nicht kleiner als ${atLeast[1]} sein.`;
  if (/must not be empty|must not be equal to/i.test(message)) {
    return `${label} fehlt – für diesen Modus ist die Angabe nötig.`;
  }
  return `${label} ist ungültig.`;
}

/** Ein Befund des Servers als deutscher Satz am Feld und im Abschnitt. */
export function serverIssue(issue: PricingConfigurationValidationIssue): FieldIssue {
  const coded = CODE_TEXT[issue.code];
  const known = KNOWN[issue.message];
  const field = coded?.[0] || (issue.field ? fieldRoot(issue.field) : '') || known?.[0] || 'mode';
  const label = FIELD_LABEL[field] ?? field;
  if (coded || known) {
    const text = coded?.[1] ?? (known![0] ? known![1] : `${label} ${known![1]}`);
    return { field, section: sectionOfField(field), message: text, source: 'server' };
  }
  // Die Währung des Apartments ist Stammdatum und hier nicht änderbar.
  const message =
    field === 'apartmentCurrency'
      ? 'Die Währung des Apartments fehlt oder ist ungültig – bitte in den Stammdaten prüfen.'
      : fallback(label, issue.message);
  return {
    field,
    section: sectionOfField(field),
    message,
    source: 'server',
    ...(issue.message ? { detail: issue.message } : {}),
  };
}

/** Speicherfehler und Vorschau-Sperren ohne Doppel (der Server meldet manche in beiden Listen). */
export function serverIssues(
  saveErrors: readonly PricingConfigurationValidationIssue[],
  previewBlockers: readonly PricingConfigurationValidationIssue[] = [],
): FieldIssue[] {
  const seen = new Set<string>();
  return [...saveErrors, ...previewBlockers].map(serverIssue).filter((issue) => {
    const key = `${issue.field}|${issue.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
